import PageHero from "@/components/PageHero"
import { useScrollReveal } from "@/hooks/useScrollReveal"
import {Headphones, MessageSquare, BookOpen, PenTool, Brain, Repeat, Rocket, CheckCircle} from 'lucide-react'

const habilidades = [
  { icon: Headphones, title: "Listening", text: "Treinamento auditivo com conteúdos reais, podcasts, diálogos e exercícios de compreensão para desenvolver o ouvido.", color: "#0E8EA0" },
  { icon: MessageSquare, title: "Speaking", text: "Prática de conversação com aulas ao vivo (EW Live), exercícios de pronúncia e interação com a Lucy IA.", color: "#B038B2" },
  { icon: BookOpen, title: "Reading", text: "Leitura de textos graduados, artigos e materiais contextualizados para expandir vocabulário e compreensão.", color: "#F5C542" },
  { icon: PenTool, title: "Writing", text: "Exercícios de escrita com correção e feedback, desde frases simples até textos complexos e e-mails profissionais.", color: "#6CC4C4" },
]

const etapas = [
  { icon: Brain, title: "Diagnóstico", text: "Avaliação inicial para identificar seu nível atual e criar um plano de estudos personalizado." },
  { icon: BookOpen, title: "Conteúdo estruturado", text: "Mais de 400 horas de aulas na EW Academy, organizadas por nível e habilidade." },
  { icon: Repeat, title: "Prática constante", text: "Exercícios diários, aulas ao vivo semanais e interação com a Lucy IA 24h." },
  { icon: Rocket, title: "Evolução mensurável", text: "Acompanhamento de progresso, certificados e marcos de evolução em cada nível." },
]

export default function MetodoGO() {
  const section1 = useScrollReveal()
  const section2 = useScrollReveal()
  const section3 = useScrollReveal()

  return (
    <>
      <PageHero
        title="Método GO"
        subtitle="Uma metodologia própria que equilibra as quatro habilidades essenciais do idioma com tecnologia e prática constante."
      />

      {/* Intro */}
      <section className="py-20 bg-white">
        <div
          ref={section1.ref}
          className={`max-w-5xl mx-auto px-5 transition-all duration-700 ${section1.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: "#0E8EA0" }}>
              Aprenda com método, evolua com prática
            </h2>
            <p className="text-gray-600 leading-relaxed">
              O Método GO da English Work foi desenvolvido para equilibrar as quatro habilidades essenciais do idioma — ouvir, falar, ler e escrever — com foco em evolução consistente, aplicação prática e autonomia do aluno. A estrutura pedagógica segue parâmetros internacionais de proficiência.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {habilidades.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 text-center group"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon size={28} style={{ color: item.color }} />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Etapas */}
      <section className="py-20" style={{ backgroundColor: "#F8F9FA" }}>
        <div
          ref={section2.ref}
          className={`max-w-5xl mx-auto px-5 transition-all duration-700 ${section2.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ color: "#0E8EA0" }}>
            Como funciona na prática
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {etapas.map((item, i) => (
              <div key={item.title} className="relative">
                <div className="bg-white rounded-2xl p-6 border border-gray-100 h-full">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm mb-4"
                    style={{ backgroundColor: "#B038B2" }}
                  >
                    {i + 1}
                  </div>
                  <h3 className="text-base font-bold mb-2 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 bg-white">
        <div
          ref={section3.ref}
          className={`max-w-4xl mx-auto px-5 transition-all duration-700 ${section3.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: "#0E8EA0" }}>
            Por que o Método GO funciona
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Metodologia baseada em parâmetros internacionais de proficiência",
              "Equilíbrio entre as 4 habilidades do idioma",
              "Tecnologia como aliada, não como distração",
              "Suporte contínuo com Lucy IA 24h",
              "Aulas ao vivo semanais para prática real",
              "Acompanhamento de progresso e certificados",
              "Conteúdo atualizado e contextualizado",
              "Flexibilidade total para estudar no seu ritmo",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                <CheckCircle size={20} className="shrink-0 mt-0.5" style={{ color: "#0E8EA0" }} />
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
